import React, { useEffect, useState, useCallback } from 'react';
import type { ReactElement } from 'react';
import cIf from 'classnames';
import moment from 'moment';
import TranslatorManager from 'ts-translate-js';
import type { CartStatus, CompleteCartStatus } from '../../../mvcapi/models/cart';
import { useCartControl } from '../../../logic/hooks/use-cart-control';

const tr = TranslatorManager.boundTR;

/**
 * Renders a standalone cart time
 * @returns A very-simple component containing the cart timer
 */ 
export function CartTimer(): ReactElement {
    const [cartStatus, setCartStatus] = useState<CartStatus>(null);
    const [serverTimeDiff, setServerTimeDiff] = useState<any>(null);
    const [timer, setTimer] = useState<string>(null);
    const [displayTime, setDisplayTime] = useState<boolean>(true);
    const [expiresSoon, setExpiresSoon] = useState<boolean>(false);
    const [cartExpired, setCartExpired] = useState<boolean>(false);

    const cartControl = useCartControl();
    
    const getCartStatus = useCallback(async (): Promise<void> => {
        const response: CompleteCartStatus = await tsapp.mvcapi.postCtl('cart/cartstatus');
        setCartStatus(response.cartStatus);
        setServerTimeDiff(moment(response.cartStatus.serverTimeUTC).diff(moment.utc()));
    }, []);

    const refreshTimer = () => {
        let productCount;
        
        let timeString = '0:00';
        if (cartStatus != null) {
            productCount = cartStatus.ticketCount;
        }
    
        if (productCount > 0) {
            const currentTimeMatched = moment.utc().add(serverTimeDiff, "ms"); // Time matched to server time. This is for computers that have a big difference with the server time
    
            // Get expiry date
            const expiryDateUTC = moment.utc(cartStatus.expiryDateUTC);
    
            // Get the diff!
            const remainingTimeMS = expiryDateUTC.diff(currentTimeMatched);
    
            if(remainingTimeMS <= (3 * 60 * 1000)) {
                setDisplayTime(!displayTime);
                setExpiresSoon(true);
            }
    
            if(remainingTimeMS > 0) {
                timeString = moment.utc(remainingTimeMS).format("m:ss"); // Build the time string
                setTimer(timeString);
            }
            else {
                setCartExpired(true); // So we suppose that there are no tickets in the cart anymore...
            }
        }
    };
    
    const timerInterval = setInterval(function() { refreshTimer(); }, 1000);

    useEffect(() => {
        cartControl.onRefreshCart(getCartStatus);
        if (cartStatus == undefined) {

            (async () => {
                await getCartStatus();
            })();
        }

        return () => {
            clearInterval(timerInterval);
            cartControl.offRefreshCart(getCartStatus);
        };
    }, [getCartStatus, timerInterval, cartStatus, cartControl]);

    let timeLabel;
    
    if(cartStatus?.ticketCount > 0) {
        timeLabel = <div className={cIf([ "time", { expiressoon: expiresSoon, flash: expiresSoon && displayTime } ])}>
            <div>{timer}</div>
        </div>;
    } else {
        if(cartExpired)
            timeLabel = <div className="time elapsed"><div>{tr("cartwidget.timeelapsed")}</div></div>;
    }

    if (timeLabel != undefined) {
        // Display the timer (needs to be properly set up in css, though).
        return <div className="displayed-timer">{timeLabel}</div>;
    }
    
    // Hide the timer (needs to be properly set up in css, though).
    return <div className="hidden-timer" />;
}